import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrash,
  faPrint,
  faQrcode,
  faRotate,
  faMagnifyingGlass,
  faClipboardList,
  faNoteSticky,
  faCircle,
  faClockRotateLeft,
  faTruckRampBox,
  faUserCheck,
  faUserMinus,
  faPlay,
  faPaperPlane,
  faTriangleExclamation,
  faRotateLeft,
  faCircleCheck,
  faCircleXmark,
  faLock,
  faPaperclip,
  faFileLines,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { auditLogService } from '@/services/audit-log.service';
import { SkeletonList } from '@/components/skeletons';

interface Props {
  equipmentId: string;
}

/**
 * D-104 (Stage D) — the journey vocabulary.
 *
 * The backend timeline now also returns events reached through the equipment's
 * inspections and inwards, which is what makes this a journey rather than a
 * list of equipment edits. Those events were previously invisible, so they had
 * no labels and fell through to their raw names ("inspection.operations.returned").
 *
 * Every key below is an event name that genuinely exists in the audit log. None
 * is speculative, and no Service or Dispatch event is listed — those modules do
 * not exist.
 */
const EVENT_LABELS: Record<string, string> = {
  'equipment.created': 'Equipment Created',
  'equipment.updated': 'Equipment Updated',
  'equipment.deleted': 'Equipment Deleted',
  'equipment.label-printed': 'Label Printed',
  'equipment.qr-generated': 'QR Code Generated',
  'equipment.qr-regenerated': 'QR Code Regenerated',
  'equipment.scanned': 'QR Code Scanned',

  // ── Inward ──────────────────────────────────────────────────────────────
  'equipment-intake.created': 'Inward Received',
  'equipment-intake.updated': 'Inward Updated',
  'equipment-intake.ship-to-changed': 'Inward Ship-To Changed',

  // ── Inspection ──────────────────────────────────────────────────────────
  'inspection.created': 'Inspection Created',
  'inspection.updated': 'Inspection Updated',
  'inspection.assigned': 'Engineer Assigned',
  'inspection.claimed': 'Claimed by Engineer',
  'inspection.unclaimed': 'Released by Engineer',
  'inspection.started': 'Inspection Started',
  'inspection.finding.created': 'Finding Recorded',
  'inspection.finding.updated': 'Finding Updated',
  'inspection.attachment.uploaded': 'Evidence Uploaded',
  'inspection.attachment.deleted': 'Evidence Removed',
  'inspection.submitted': 'Submitted for Review',
  'inspection.warranty-status.changed': 'Warranty Status Changed',
  'inspection.note.created': 'Internal Note Added',

  // ── Operations review ───────────────────────────────────────────────────
  'inspection.operations.returned': 'Returned by Operations',
  'inspection.operations.approved': 'Approved by Operations',

  // ── Report ──────────────────────────────────────────────────────────────
  'inspection.report.draft-created': 'Report Draft Created',
  'inspection.report.generated': 'Report Generated',
  'inspection.report.submitted': 'Report Submitted for Review',
  'inspection.report.approved': 'Report Approved',
  'inspection.report.rejected': 'Report Rejected',
  'inspection.report.reopened': 'Report Reopened',
  'inspection.report.published': 'Report Published to Customer',
  'inspection.report.downloaded': 'Report Downloaded',
  'inspection.report.customer-downloaded': 'Report Downloaded by Customer',

  // ── Terminal ────────────────────────────────────────────────────────────
  'inspection.closed': 'Job Closed',
  'inspection.cancelled': 'Inspection Cancelled',
};

const NEUTRAL = 'bg-muted text-muted-foreground';
const PROGRESS = 'bg-accent/15 text-accent';
const ATTENTION = 'bg-amber-500/15 text-amber-700';
const GOOD = 'bg-green-500/15 text-green-700';
const BAD = 'bg-destructive/15 text-destructive';

const EVENT_STYLES: Record<string, { icon: IconDefinition; dot: string }> = {
  'equipment.created': { icon: faPlus, dot: 'bg-primary/15 text-primary' },
  'equipment.updated': { icon: faPen, dot: PROGRESS },
  'equipment.deleted': { icon: faTrash, dot: BAD },
  'equipment.label-printed': { icon: faPrint, dot: NEUTRAL },
  'equipment.qr-generated': { icon: faQrcode, dot: NEUTRAL },
  'equipment.qr-regenerated': { icon: faRotate, dot: NEUTRAL },
  'equipment.scanned': { icon: faMagnifyingGlass, dot: NEUTRAL },

  'equipment-intake.created': { icon: faTruckRampBox, dot: 'bg-primary/15 text-primary' },
  'equipment-intake.updated': { icon: faPen, dot: NEUTRAL },
  'equipment-intake.ship-to-changed': { icon: faPen, dot: NEUTRAL },

  'inspection.created': { icon: faClipboardList, dot: PROGRESS },
  'inspection.updated': { icon: faPen, dot: NEUTRAL },
  'inspection.assigned': { icon: faUserCheck, dot: PROGRESS },
  'inspection.claimed': { icon: faUserCheck, dot: PROGRESS },
  'inspection.unclaimed': { icon: faUserMinus, dot: NEUTRAL },
  'inspection.started': { icon: faPlay, dot: PROGRESS },
  'inspection.finding.created': { icon: faTriangleExclamation, dot: ATTENTION },
  'inspection.finding.updated': { icon: faTriangleExclamation, dot: NEUTRAL },
  'inspection.attachment.uploaded': { icon: faPaperclip, dot: NEUTRAL },
  'inspection.attachment.deleted': { icon: faPaperclip, dot: NEUTRAL },
  'inspection.submitted': { icon: faPaperPlane, dot: PROGRESS },
  'inspection.warranty-status.changed': { icon: faShieldHalved, dot: NEUTRAL },
  'inspection.note.created': { icon: faNoteSticky, dot: 'bg-primary/10 text-primary' },

  'inspection.operations.returned': { icon: faRotateLeft, dot: ATTENTION },
  'inspection.operations.approved': { icon: faCircleCheck, dot: GOOD },

  'inspection.report.draft-created': { icon: faFileLines, dot: NEUTRAL },
  'inspection.report.generated': { icon: faFileLines, dot: PROGRESS },
  'inspection.report.submitted': { icon: faPaperPlane, dot: PROGRESS },
  'inspection.report.approved': { icon: faCircleCheck, dot: GOOD },
  'inspection.report.rejected': { icon: faCircleXmark, dot: BAD },
  'inspection.report.reopened': { icon: faRotateLeft, dot: ATTENTION },
  'inspection.report.published': { icon: faCircleCheck, dot: GOOD },
  'inspection.report.downloaded': { icon: faFileLines, dot: NEUTRAL },
  'inspection.report.customer-downloaded': { icon: faFileLines, dot: NEUTRAL },

  'inspection.closed': { icon: faLock, dot: GOOD },
  'inspection.cancelled': { icon: faCircleXmark, dot: BAD },
};

function getStyle(eventName: string): { icon: IconDefinition; dot: string } {
  return EVENT_STYLES[eventName] ?? { icon: faCircle, dot: 'bg-muted text-muted-foreground' };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function EquipmentTimeline({ equipmentId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['equipment-timeline', equipmentId],
    queryFn: () => auditLogService.getEquipmentTimeline(equipmentId),
    staleTime: 30_000,
  });

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faClockRotateLeft} className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">Activity Timeline</h2>
        {data && (
          <span className="ml-auto text-xs text-muted-foreground">{data.total} event{data.total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {isLoading && <SkeletonList rows={4} />}

      {!isLoading && (!data || data.items.length === 0) && (
        <div className="py-8 text-center">
          <FontAwesomeIcon icon={faClockRotateLeft} className="mb-2 h-8 w-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
        </div>
      )}

      {!isLoading && data && data.items.length > 0 && (
        <div className="space-y-0">
          {data.items.map((entry, i) => {
            const style = getStyle(entry.eventName);
            const label = EVENT_LABELS[entry.eventName] ?? entry.eventName;
            return (
              <div key={entry.id} className="relative flex gap-3 pb-4">
                {i < data.items.length - 1 && (
                  <div className="absolute left-4 top-8 bottom-0 w-px bg-border" />
                )}
                <div
                  className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${style.dot} ring-2 ring-card`}
                >
                  <FontAwesomeIcon icon={style.icon} className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 pt-1 min-w-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium text-foreground">{label}</span>
                    <span className="flex-shrink-0 text-xs text-muted-foreground">
                      {formatDate(entry.occurredAt)}
                    </span>
                  </div>
                  {entry.actorName && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      by {entry.actorName}
                      {entry.actorRoleName && (
                        <span className="text-muted-foreground/70"> ({entry.actorRoleName})</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
